import {
	isDisplayingSendTo,
	onClickRollupTreeGroup,
	renderDatasetActions,
	renderRollupTreeGroup,
	renderGroup,
	renderUser,
	renderUsers
} from './permissions-audit-util'
import DomainType from 'scoreboard/components/domain-type'
const {
	DATASET_PERMISSIONS_AUDIT,
	USER,
	GROUP,
	ROLLUP_TREE_GROUP
} = DomainType
import template from './dataset-permissions-audit.hbs'
import appDisplay from '../components/app-display'
import $ from 'jquery'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import DatasetsFilterBadges from '../datasets/filters/datasets-filter-badges'
import Dataset from '../datasets/model/dataset'
import EditableButton from '../components/editable-button'
import ObjectSelectorForm from '../components/object-selector-form'
import * as datasetSelectorOptions from '../datasets/dataset-selector-options'
import conductor from '../components/conductor'
import OrgPermissionsAuditTable
	from './org-permissions-audit/org-permissions-audit-table'
import PermissionsAudit from './permissions-audit'
import wrapModel from '../components/wrap-model'
import LoadingDots from 'scoreboard/components/loading-dots'
import diagnostics from '../components/diagnostics'

const DatasetPermissionsAudit = PermissionsAudit.extend(/** @lends {DatasetPermissionsAudit} */{

	className: 'admin permissions-audit table-hack display-layout',

	events: _.extend({}, PermissionsAudit.prototype.events, {
		// #56609 only set up this handler for advanced permissions. For
		// standard permissions, the event handler is handled by
		// OrgPermissionsAuditTable
		'click .advanced.panel .template': 'onClickRollupTreeGroup',
		'click .set-dataset': 'onClickSetDataset'
	}),

	/**
	 * The Dataset Permissions Audit (#54664)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Number} options.datasetId the ID of the dataset whose permissions
	 * are to be audited
	 */
	initialize: function(options = {}) {
		this.options = options
		this.datasetId = options.datasetId
	},

	/**
	 * @override
	 */
	domainObjectInfo () {
		return {
			domainId: this.datasetId,
			domainType: DATASET_PERMISSIONS_AUDIT
		}
	},

	render: function() {
		this.$el.html(template())
		this.renderDatasetButton()
		this.fetchAndRenderResponse()
		return this
	},

	renderDatasetButton: function() {
		this.datasetButton = new EditableButton({
			el: this.$('.set-dataset'),
			message: 'datasets.choose'
		}).render()
	},

	fetchAndRenderResponse: function() {
		this.$('.dataset-permissions-audit-table-container').empty()

		this.loadingDots = new LoadingDots({
			el: this.$('.loading-dots-container')[0]
		}).render()

		this.fetch().done(response => {
			// delete the promise so that we'll know we need to retrieve data
			// the next time an org is selected
			delete this.fetchPromise
			this.renderResponse(response)
			this.trigger('ready')
			this.loadingDots?.clear()
		})
		return this.fetchPromise
	},

	fetch: function() {
		if (this.fetchPromise) return this.fetchPromise

		let datasetId = this.datasetId
		let promise
		if (datasetId && datasetId != '') {
			promise = $.get(`api/admin/dataset-permissions-audit/${datasetId}`)
		} else {
			promise = $.Deferred().resolve()
		}
		return promise
	},

	renderResponse: function(response) {
		if (_.isNil(response)) return

		this.$('.panel-with-table').removeClass('is-not-displayed')

		this.response = response
		// needed for renderDatasetsFilterBadges
		this.dataset = new Dataset(response.dataset)

		this.renderDatasetLabel()
		this.updateAdvancedBanner()
		this.toggleTables()
		this.$('.send-to').toggleClass('is-not-displayed',
			!isDisplayingSendTo(this.datasetId, diagnostics.exportEnabled))
		if (this.isStandard()) {
			this.orgPermissionsAuditTable?.remove()
			this.orgPermissionsAuditTable = new OrgPermissionsAuditTable({
				response,
				organizationId: this.dataset.get('organizationId')
			})
			this.$('.dataset-permissions-audit-table-container').html(this.orgPermissionsAuditTable.el)
			this.orgPermissionsAuditTable.render()
		} else {
			this.treeItemsById = _.keyBy(response.treeItems, 'id')
			let permissions = DatasetPermissionsAudit.getSortedPermissions(
				this.response.permissions)
			this.renderTable(permissions)
			this.renderDatasetsFilterBadges(permissions)
		}
		this.updateExportHeader({
			selectionTitle: this.dataset?.get('label'),
			pageTitle: i18n.get('admin.datasetPermissionsAudit')
		})
	},

	/** Renders the dataset name on the "choose a dataset" button */
	renderDatasetLabel: function() {
		this.datasetButton.renderLabel(this.dataset?.get('label'))
	},

	/**
	 * Toggles the visibility of the tables used to display permissions for
	 * datasets with standard vs advanced permissions
	 */
	toggleTables: function() {
		this.$('.advanced.panel')
			.toggleClass('is-not-displayed', this.isStandard())
		this.$('.dataset-permissions-audit-table-container')
			.toggleClass('is-not-displayed', !this.isStandard())
	},

	/**
	 * Renders a second banner under the datasets selector when a dataset with
	 * advanced permissions is selected. Hides the extra banner when a dataset
	 * with standard permissions is selected
	 */
	updateAdvancedBanner: function() {
		let linkLabel = i18n.get('datasets.datasets') + ' > ' +
			i18n.get('common.edit')
		let link = `<a
			class="sp-navigate text-link"
			href="datasets/${this.datasetId}/edit">${_.escape(linkLabel)}</a>`
		this.$('.advanced.banner')
			.toggleClass('is-not-displayed', this.isStandard())
			.html(i18n.get('admin.datasetPermissionsAudit.banner.advanced', link))
	},

	/** Returns whether current dataset is a standard permissions dataset */
	isStandard: function() {
		return this.dataset?.hasStandardPermissions()
	},

	/**
	 * Renders the table of permissions for datasets with advanced permissions
	 */
	renderTable: function(sortedPermissions) {
		let html = ''
		_.each(sortedPermissions, permission => {
			html += '<tr>' +
				'<td>' + this.renderPermissionSource(permission.principal) + '</td>' +
				'<td>' + renderUsers(this.response.users, permission.userIds) + '</td>' +
				'<td class="datasets-filter-badges-container"></td>' +
				'<td>' + renderDatasetActions(permission) + '</td>' +
			'</tr>'
		})
		this.$('tbody').html(html)
	},

	/**
	 * Renders the permission source column for datasets with advanced
	 * permissions
	 */
	renderPermissionSource: function(principal) {
		if (!principal) return ''

		if (principal.type == USER) {
			return renderUser(principal)
		} else if (principal.type == GROUP) {
			return renderGroup(principal)
		} else {
			return renderRollupTreeGroup({
				rollupTreeGroupId: principal.id,
				rollupTreeGroupLabel: principal.rollupTreeGroupLabel,
				treeRootId: principal.treeRootId,
				fieldLabel: principal.fieldLabel
			})
		}
	},

	/**
	 * Renders the "Permission Filters" column of the "Dataset Permissions" panel
	 */
	renderDatasetsFilterBadges: function(permissions) {
		_.invokeMap(this.datasetsFilterBadges, 'remove')
		this.datasetsFilterBadges = []
		_.each(this.$('.datasets-filter-badges-container'), (el, i) => {
			let permission = permissions[i]
			let filterBadges = new DatasetsFilterBadges({
				model: this.dataset,
				filters: permission?.filters,
				treeItemsById: this.treeItemsById,
				showPermissionFilters: true,
				noneMessage: '',
				isEditable: false,
				clickable: false,
				filterTextOptions: {
					showBuiltInRollupTreeGroupLabel: true
				}
			})
			this.datasetsFilterBadges[i] = filterBadges
			this.$(el).append(filterBadges.el)
			filterBadges.render()
		})
	},

	onClickSetDataset: function() {
		this.datasetSelectorForm?.remove()
		this.datasetSelectorForm = new ObjectSelectorForm(_.extend(datasetSelectorOptions.forSelector(), {
			title: i18n.get('datasets.choose'),
			id: this.datasetId,
			orgId: this.dataset?.get('organizationId'),
			projectId: this.dataset?.get('projectId'),
			primaryButtonKey: 'common.chooseNoEllipsis'
		}))
		appDisplay.dialog(this.datasetSelectorForm)

		this.datasetSelectorForm.on('select', dataset => {
			this.datasetId = dataset?.id
			this.dataset = wrapModel(dataset, Dataset)
			this.renderDatasetLabel()
			conductor.navigate(`admin/dataset-permissions-audit/${dataset.id}`)
			$.when(this.fetchAndRenderResponse()).done(() => {
				this.datasetSelectorForm.trigger('done') // hide the dialog
			})
		})
	},

	onClickRollupTreeGroup

}, {

	display: function(datasetId) {
		let view = new DatasetPermissionsAudit({ datasetId })
		appDisplay.display(view.fetch(), () => view)
	},

	/**
	 * Sorts the permissions first by principle type (user, group, tree
	 * template), then alphabetically by principal label
	 */
	getSortedPermissions: function(permissions) {
		let typeOrder = {
			[USER]: 1,
			[GROUP]: 2,
			[ROLLUP_TREE_GROUP]: 3
		}
		return _.sortBy(permissions, permission => {
			let principal = permission.principal
			return typeOrder[principal.type] + principal.label
		})
	}

})

export default DatasetPermissionsAudit

DatasetPermissionsAudit.__filename__ = 'admin/dataset-permissions-audit.js'
